import type React from 'react';
import { useCallback, useRef } from 'react';
import type { OnChangeStageSettings, OnCommitStageSettings, StageSettingsT } from '../types';
type IReturn = {
  stageSettingsRef: React.RefObject<StageSettingsT>;
  onChangeStageSettings: OnChangeStageSettings;
  onCommitStageSettings: OnCommitStageSettings;
};
type PropsT = {
  stageSettings: StageSettingsT;
  setStageSettings: (value: StageSettingsT | ((prev: StageSettingsT) => StageSettingsT)) => void;
};
export function useStageSettings({
  stageSettings: stageSettingsProp,
  setStageSettings: setStageSettingsProp
}: PropsT): IReturn {
  const liveStageSettingsRef = useRef<StageSettingsT>(stageSettingsProp);
  const handleChangeStageSettings = useCallback((value: Partial<StageSettingsT> | ((prev: StageSettingsT) => StageSettingsT)) => {
    const updatedValue = typeof value === 'function' ? value(liveStageSettingsRef.current) : value;

    // If value function returns same value as liveStageSettingsRef.current, don't update it.
    if (updatedValue === liveStageSettingsRef.current) {
      return;
    }
    liveStageSettingsRef.current = {
      ...liveStageSettingsRef.current,
      ...updatedValue
    };
  }, []);
  const handleCommitStageSettings = useCallback((value: Partial<StageSettingsT> | ((prev: StageSettingsT) => StageSettingsT)) => {
    handleChangeStageSettings(value || liveStageSettingsRef.current);
    setStageSettingsProp?.(liveStageSettingsRef.current);
  }, [handleChangeStageSettings, setStageSettingsProp]);
  return {
    stageSettingsRef: liveStageSettingsRef,
    onChangeStageSettings: handleChangeStageSettings,
    onCommitStageSettings: handleCommitStageSettings
  };
}